import React from 'react';
import { ProjectDetails } from '../types';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/UI/Button';

interface ProjectHeaderProps {
  project: ProjectDetails;
}

const ProjectHeader = ({ project }: ProjectHeaderProps) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-teal-500/10 text-teal-700 border-teal-500/20';
      case 'in-progress':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'maintenance':
        return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
      default:
        return 'bg-slate-500/10 text-slate-700 border-slate-500/20';
    }
  };

  return (
    <div className="mb-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
        <div className="flex-1 space-y-6">
          {/* Status & Categories */}
          <div className="flex flex-wrap items-center gap-4">
            <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.15em] border ${getStatusColor(project.status)} shadow-sm`}>
              {project.status.replace('-', ' ')}
            </span>
            <div className="flex items-center gap-2 px-4 py-1.5 bg-white border border-[#0C2C39]/10 rounded-full shadow-sm">
              <Icon name="Layers" size={12} className="text-[#0D9488]" />
              <span className="text-[10px] font-black text-[#0C2C39]/60 uppercase tracking-[0.15em]">
                {project.categories.join(' & ')}
              </span>
            </div>
          </div>

          {/* Title & Subtitle */}
          <div className="space-y-4">
            <h1 className="text-5xl md:text-7xl font-black text-[#0C2C39] leading-[0.9] uppercase tracking-tighter">
              {project.title.split(' ').map((word, i) => (
                <span key={i} className={i % 2 === 1 ? 'text-[#0D9488]' : ''}>
                  {word}{' '}
                </span>
              ))}
            </h1>
            {project.subtitle && (
              <p className="text-xl md:text-2xl font-bold text-[#0C2C39]/40 uppercase tracking-tight italic">
                {project.subtitle}
              </p>
            )}
          </div>

          {/* Primary Metadata Bar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 pt-8 border-t border-[#0C2C39]/5">
            {project.completionDate && project.completionDate !== 'N/A' && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={14} className="text-[#0D9488]" />
                  <span className="text-[10px] font-black text-[#0C2C39]/40 uppercase tracking-[0.2em]">Completion</span>
                </div>
                <p className="text-[#0C2C39] font-black text-lg">
                  {project.completionDate}
                </p>
              </div>
            )}

            {project.client && project.client !== 'Personal Project' && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon name="Building" size={14} className="text-[#0D9488]" />
                  <span className="text-[10px] font-black text-[#0C2C39]/40 uppercase tracking-[0.2em]">Client</span>
                </div>
                <p className="text-[#0C2C39] font-black text-lg line-clamp-1">
                  {project.client}
                </p>
              </div>
            )}

            {project.duration && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={14} className="text-[#0D9488]" />
                  <span className="text-[10px] font-black text-[#0C2C39]/40 uppercase tracking-[0.2em]">Duration</span>
                </div>
                <p className="text-[#0C2C39] font-black text-lg">
                  {project.duration}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row lg:flex-col items-stretch gap-4 lg:min-w-[280px]">
          {project.links.liveDemo && (
            <Button
              variant="default"
              className="bg-[#0C2C39] text-white hover:bg-[#0D9488] px-8 py-6 rounded-2xl font-black uppercase tracking-[0.2em] text-[11px] shadow-[8px_8px_0px_0px_#0D948820] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all"
              onClick={() => window.open(project.links.liveDemo, '_blank')}
            >
              <div className="flex items-center justify-center gap-3 w-full">
                Launch Experience
                <Icon name="ArrowUpRight" size={18} />
              </div>
            </Button>
          )}

          {project.links.repository && (
            <Button
              variant="outline"
              className="border-2 border-[#0C2C39] text-[#0C2C39] hover:bg-[#0C2C39]/5 px-8 py-6 rounded-2xl font-black uppercase tracking-[0.2em] text-[11px]"
              onClick={() => window.open(project.links.repository, '_blank')}
            >
              <div className="flex items-center justify-center gap-3 w-full">
                <Icon name="Github" size={18} />
                View Repository
              </div>
            </Button>
          )}
        </div>
      </div>

      {/* Technology Stack Horizontal Strip */}
      {project.technologies.length > 0 && (
        <div className="mt-12 pt-8 border-t border-[#0C2C39]/5">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0D9488]/10 flex items-center justify-center">
                <Icon name="Cpu" size={16} className="text-[#0D9488]" />
              </div>
              <span className="text-[10px] font-black text-[#0C2C39]/40 uppercase tracking-[0.2em]">Stack</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {project.technologies.map((tech) => (
                <span
                  key={tech.id}
                  className="px-4 py-2 bg-white border border-[#0C2C39]/10 rounded-xl text-[10px] font-black text-[#0C2C39]/70 uppercase tracking-widest hover:border-[#0D9488] hover:text-[#0D9488] transition-colors cursor-default select-none"
                >
                  {tech.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectHeader;